import axios from 'axios';
import { getIncidentsApiBase } from '@/lib/incidentsApi';
import { VIBE_CHAT_API_BASE } from '@/lib/vibeChatConfig';
import { formatApiError } from '@/lib/vibeChatApi';

/** dev2 WAF (SizeRestrictions_BODY) blocks API Gateway request bodies above 8 KB. */
export const WAF_SAFE_TICKET_PUT_BODY_BYTES = 7500;

function utf8ByteLength(value) {
  return new TextEncoder().encode(value).length;
}

/** JSON body size for PUT /tickets/{id} with only chain_of_thought. */
export function ticketChainOfThoughtPutBodyBytes(chainOfThought) {
  return utf8ByteLength(JSON.stringify({ chain_of_thought: chainOfThought }));
}

/**
 * Merge CoT append text while keeping the PUT JSON body under the WAF body limit.
 * Trims oldest existing content first so the latest deploy/ATC block is preserved.
 */
export function mergeChainOfThoughtWithinByteLimit(
  existing,
  append,
  maxBodyBytes = WAF_SAFE_TICKET_PUT_BODY_BYTES,
) {
  const appendText = (append || '').trim();
  const base = (existing || '').trim();
  if (!appendText) return base;
  if (!base) {
    return ticketChainOfThoughtPutBodyBytes(appendText) <= maxBodyBytes
      ? appendText
      : appendText.slice(-Math.floor(maxBodyBytes * 0.9));
  }

  const separator = '\n\n';
  let trimmedBase = base;
  while (true) {
    const candidate = `${trimmedBase}${separator}${appendText}`;
    if (ticketChainOfThoughtPutBodyBytes(candidate) <= maxBodyBytes) {
      return candidate;
    }
    if (!trimmedBase) {
      return appendText.slice(-Math.floor(maxBodyBytes * 0.9));
    }
    const cut = Math.max(Math.floor(trimmedBase.length * 0.15), 512);
    trimmedBase = trimmedBase.slice(cut).trimStart();
  }
}

/**
 * Deploy / ATC go through incidents (gateway → incidents → master).
 * Same path as tickets/code — master stays ClusterIP-only.
 */
function getAbapApiBase() {
  const apiBase = (getIncidentsApiBase() || '').trim().replace(/\/$/, '');
  if (!apiBase) {
    throw new Error(
      'Incidents API URL is not configured (set REACT_APP_INCIDENTS_API_URL or REACT_APP_BACKEND_URL)',
    );
  }
  return apiBase;
}

/**
 * POST /abap/deploy — deploy ABAP source to SAP via incidents → master agent.
 */
export async function deployAbapCode({
  code,
  objectName,
  description = '',
  objectType = 'PROG',
  packageName = '$TMP',
  ticketNumber = '',
  ticketTypeKey = '',
}) {
  const response = await axios.post(`${getAbapApiBase()}/abap/deploy`, {
    code,
    object_name: objectName,
    object_type: objectType,
    description,
    package: packageName,
    ticket_number: ticketNumber,
    ticket_type_key: ticketTypeKey,
  });
  return response.data;
}

/**
 * POST /abap/run-atc — Run ABAP Test Cockpit on a deployed SAP object.
 */
export async function runAbapAtc({
  objectUrl = '',
  objectName = '',
  objectType = 'PROG',
  variant = '',
  ticketNumber = '',
}) {
  const response = await axios.post(`${getAbapApiBase()}/abap/run-atc`, {
    object_url: objectUrl,
    object_name: objectName,
    object_type: objectType,
    variant,
    ticket_number: ticketNumber,
  });
  return response.data;
}

const SKIPPED_ATC = {
  success: false,
  status: 'skipped',
  object_url: '',
  message: 'ATC skipped — deploy did not succeed',
  check_variant: '',
  p1: 0,
  p2: 0,
  p3: 0,
  findings: [],
  skipped_reason: 'deploy_failed',
};

/**
 * Deploy then RunATC — two calls: existing /abap/deploy, then /abap/run-atc.
 * Legacy one-shot path (tests / manual). Product Deploy & Run ATC uses
 * {@link startAbapAtcRemediation} instead.
 */
export async function deployAbapCodeAndRunAtc(params) {
  const deployResult = await deployAbapCode(params);

  if (!deployResult.success) {
    return {
      ...deployResult,
      atc: SKIPPED_ATC,
      atc_status: SKIPPED_ATC.status,
      atc_message: SKIPPED_ATC.message,
      atc_skipped_reason: SKIPPED_ATC.skipped_reason,
      check_variant: '',
      atc_p1: 0,
      atc_p2: 0,
      atc_p3: 0,
      atc_findings: [],
    };
  }

  const atcResult = await runAbapAtc({
    objectUrl: deployResult.object_url || '',
    objectName: deployResult.object_name || params.objectName,
    objectType: deployResult.object_type || params.objectType || 'PROG',
    ticketNumber: params.ticketNumber || '',
  });

  return {
    ...deployResult,
    atc: atcResult,
    atc_status: atcResult.status,
    atc_message: atcResult.message,
    atc_skipped_reason: atcResult.skipped_reason || '',
    check_variant: atcResult.check_variant || '',
    atc_p1: atcResult.p1 ?? 0,
    atc_p2: atcResult.p2 ?? 0,
    atc_p3: atcResult.p3 ?? 0,
    atc_findings: atcResult.findings || [],
    object_url: atcResult.object_url || deployResult.object_url || '',
  };
}

/** Terminal ticket stages set by atc_remediation_orchestrator. */
export const ATC_REMEDIATION_TERMINAL_STAGES = Object.freeze(['atc_clean', 'atc_partial']);

export const ATC_REMEDIATION_STAGE = Object.freeze({
  REMEDIATING: 'atc_remediating',
  CLEAN: 'atc_clean',
  PARTIAL: 'atc_partial',
});

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * POST /abap/deploy-and-remediate-atc — start ATC remediation (async by default).
 * Progress streams to ticket chain_of_thought; poll ticket stage for completion.
 */
export async function startAbapAtcRemediation({
  code,
  objectName,
  description = '',
  objectType = 'PROG',
  packageName = '$TMP',
  transport = '',
  ticketNumber = '',
  originalRequest = '',
  maxAttempts = 5,
  // Empty → master uses ABAP_ATC_VARIANT / Z_HALO_ABAP_REPORT (do not hardcode DEFAULT).
  variant = '',
  asyncRun = true,
}) {
  const response = await axios.post(`${getAbapApiBase()}/abap/deploy-and-remediate-atc`, {
    code,
    object_name: objectName,
    object_type: objectType,
    description,
    package: packageName,
    transport,
    ticket_number: ticketNumber,
    original_request: originalRequest,
    max_attempts: maxAttempts,
    variant,
    async_run: asyncRun,
  });
  return response.data;
}

/**
 * Poll ticket until ATC remediation reaches a terminal stage (or timeout).
 */
export async function waitForAbapAtcRemediation({
  ticketNumber,
  fetchTicket,
  fetchCode,
  intervalMs = 2500,
  timeoutMs = 15 * 60 * 1000,
  onTick,
}) {
  if (!ticketNumber) {
    throw new Error('ticketNumber is required to wait for ATC remediation');
  }
  if (typeof fetchTicket !== 'function') {
    throw new Error('fetchTicket callback is required');
  }

  const started = Date.now();
  await sleep(Math.min(intervalMs, 1500));

  while (Date.now() - started < timeoutMs) {
    const ticket = await fetchTicket();
    if (onTick) onTick(ticket);
    const stage = ticket?.abap_workflow_stage || '';
    if (ATC_REMEDIATION_TERMINAL_STAGES.includes(stage)) {
      let code = '';
      if (typeof fetchCode === 'function') {
        try {
          code = (await fetchCode()) || '';
        } catch {
          code = '';
        }
      }
      return {
        stage,
        success: stage === ATC_REMEDIATION_STAGE.CLEAN,
        ticket,
        code,
        objectName: ticket?.deployed_object_name || '',
      };
    }
    await sleep(intervalMs);
  }

  throw new Error(
    `ATC remediation timed out after ${Math.round(timeoutMs / 1000)}s — check ticket chain of thought`,
  );
}

/**
 * POST /chat/{sessionId}/deploy — deploy validated ABAP from a studio-with-context chat session.
 */
export async function deployChatStudioCode(sessionId, {
  objectName = '',
  objectType = 'PROG',
  description = '',
  packageName = '$TMP',
  transport = '',
  skipActivate = false,
  skipSyntaxCheck = false,
} = {}) {
  const apiBase = VIBE_CHAT_API_BASE;
  if (!apiBase) {
    throw new Error(
      'Chat API URL is not configured (set REACT_APP_VIBE_CODING_STUDIO_URL or REACT_APP_BACKEND_URL)',
    );
  }
  if (!sessionId) {
    throw new Error('sessionId is required');
  }

  const body = {
    object_type: objectType,
    description,
    package: packageName,
    transport,
    skip_activate: skipActivate,
    skip_syntax_check: skipSyntaxCheck,
  };
  if (objectName.trim()) {
    body.object_name = objectName.trim();
  }

  const response = await axios.post(`${apiBase.replace(/\/$/, '')}/chat/${sessionId}/deploy`, body);
  return response.data;
}

export { formatApiError };

/** Flatten nested ATC findings (objects[] or findings[] wrappers). */
export function flattenAtcFindings(findings) {
  const rows = [];
  (findings || []).forEach((item) => {
    if (!item || typeof item !== 'object') return;
    if (Array.isArray(item.findings) && item.findings.length > 0) {
      item.findings.forEach((finding) => {
        if (finding && typeof finding === 'object') {
          rows.push({
            ...finding,
            object_name: finding.object_name || item.name || item.object_name || '',
            object_type: finding.object_type || item.type || item.object_type || '',
          });
        }
      });
      return;
    }
    if (
      item.priority != null
      || item.message_title
      || item.message
      || item.check_title
    ) {
      rows.push(item);
    }
  });
  return rows;
}

function countAtcPriorities(findings) {
  let p1 = 0;
  let p2 = 0;
  let p3 = 0;
  findings.forEach((finding) => {
    const pr = Number(finding.priority || finding.severity || finding.prio || 0);
    if (pr === 1) p1 += 1;
    else if (pr === 2) p2 += 1;
    else if (pr === 3) p3 += 1;
  });
  return { p1, p2, p3 };
}

/** One finding line with enough detail for a future corrector agent. */
export function formatAtcFindingLine(finding) {
  const priority = finding.priority || finding.severity || finding.prio || '?';
  const title = finding.message_title || finding.message || finding.text || 'Finding';
  const check = finding.check_title || finding.check_id || '';
  const line = finding.line != null ? `line ${finding.line}` : '';
  const location = finding.location || '';
  const messageId = finding.message_id || '';
  const objectName = finding.object_name || '';

  let head = `- [P${priority}]`;
  if (line) head += ` (${line})`;
  head += `: ${title}`;

  const meta = [];
  if (check) meta.push(`check: ${check}`);
  if (messageId) meta.push(`msg_id: ${messageId}`);
  if (objectName) meta.push(`object: ${objectName}`);
  if (location) meta.push(`loc: ${location}`);
  return meta.length ? `${head} | ${meta.join(' | ')}` : head;
}

/** Target max size for deploy+ATC summary text shown in UI and persisted to CoT. */
export const DEPLOY_ATC_SUMMARY_MAX_BYTES = 6000;

function joinLinesByteLength(lines) {
  return utf8ByteLength(lines.join('\n'));
}

/**
 * Build deploy + ATC summary text (shared by UI display and CoT persistence).
 * Always includes P1/P2/P3; adds as many finding lines as fit under maxSummaryBytes.
 */
export function formatDeployAndAtcSummary(
  result,
  { maxSummaryBytes = DEPLOY_ATC_SUMMARY_MAX_BYTES } = {},
) {
  const deployLine = (result?.message || '').trim() || 'Deploy completed.';
  const atc = result?.atc || {};
  const variant = result?.check_variant || atc.check_variant || '';
  const flattened = flattenAtcFindings(result?.atc_findings || atc.findings || []);

  let p1 = result?.atc_p1 ?? atc.p1 ?? 0;
  let p2 = result?.atc_p2 ?? atc.p2 ?? 0;
  let p3 = result?.atc_p3 ?? atc.p3 ?? 0;
  if (flattened.length && (p1 + p2 + p3) === 0) {
    ({ p1, p2, p3 } = countAtcPriorities(flattened));
  }

  let atcStatus = (result?.atc_status || atc.status || 'unknown').toUpperCase();
  if (p1 > 0) atcStatus = 'ERRORS';
  else if (p2 > 0 || p3 > 0) atcStatus = 'WARNINGS';
  else if (atcStatus === 'UNKNOWN' || atcStatus === 'PASS') atcStatus = 'PASS';

  const skipped = result?.atc_skipped_reason || atc.skipped_reason || '';
  const totalFindings = flattened.length;

  const lines = [
    'DEPLOY & RUN ATC',
    `Deploy: ${deployLine}`,
  ];

  if (skipped) {
    lines.push(`ATC: SKIPPED (${skipped})`);
    return lines.join('\n');
  }

  lines.push(
    `ATC: ${atcStatus}${variant ? ` (variant: ${variant})` : ''} — P1=${p1}, P2=${p2}, P3=${p3}`,
  );

  const findingLines = [];
  flattened.forEach((finding) => {
    const nextLine = formatAtcFindingLine(finding);
    const candidate = [...lines, ...findingLines, nextLine];
    if (joinLinesByteLength(candidate) > maxSummaryBytes) return;
    findingLines.push(nextLine);
  });
  if (findingLines.length > 0) {
    lines.push(...findingLines);
  }

  const remaining = totalFindings - findingLines.length;
  if (remaining > 0) {
    const moreLine = `… and ${remaining} more finding(s)`;
    if (joinLinesByteLength([...lines, moreLine]) <= maxSummaryBytes) {
      lines.push(moreLine);
    }
  }

  return lines.join('\n');
}

/** Summary for ticket UI (deploy panel). */
export function formatDeployAndAtcMessageForDisplay(result) {
  return formatDeployAndAtcSummary(result);
}

/** Summary for ticket chain_of_thought persistence (WAF-safe via byte cap + merge helper). */
export function formatDeployAndAtcMessageForCot(result, options = {}) {
  return formatDeployAndAtcSummary(result, options);
}
